import { useCallback, useEffect, useRef } from 'react';
import moment from 'moment';
import constants from '@/config/constants';
import { localLogoutUser } from '@/api/services/user';
import {
  clearLocalData,
  getAccessToken,
  serverLogoutUser,
} from '@/stores/auth/services';
import store, { useStoreState } from '@/stores/store';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';

export function useIdleLogout() {
  const { showNotification } = useNotification();
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const { isAuthenticated, stayLoggedIn } = useStoreState((state) => ({
    isAuthenticated: state.isAuthenticated,
    stayLoggedIn: state.stayLoggedIn,
  }));

  const handleLogout = useCallback(async () => {
    showNotification({
      title: 'Sitzung abgelaufen. Bitte melden Sie sich neu an.',
      type: 'error',
    });
    await serverLogoutUser({ jwt: (await getAccessToken()) ?? '' });
    localLogoutUser();
    clearLocalData();
    store.getActions().setLastIdleDate(null);
  }, [showNotification]);

  const resetTimerRef = useRef<() => void>(() => {});

  useEffect(() => {
    resetTimerRef.current = () => {
      if (!isAuthenticated || stayLoggedIn) {
        return;
      }

      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(
        handleLogout,
        moment.duration(constants.idleTimeToLogout, 'minutes').asMilliseconds(),
      );
    };
  }, [isAuthenticated, stayLoggedIn, handleLogout]);

  useEffect(() => {
    if (isAuthenticated && !stayLoggedIn) {
      resetTimerRef.current();
    } else {
      clearTimeout(timerRef.current);
    }
    return () => clearTimeout(timerRef.current);
  }, [isAuthenticated, stayLoggedIn]);

  return { resetTimer: () => resetTimerRef.current() };
}
